import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Plus, Users, UserPlus, Briefcase, FileBarChart, BookOpen, Shield, QrCode, Copy, X as XIcon, MessageSquare, Inbox } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../config";
import { dealerApi } from "../utils/dealerApi";
import { getPrefixByCountry, getLocalizedCountryName } from "../constants/countries";
import { getDealerTranslation } from "../translations/dealerPortal";
import { getDealerFormI18n } from "../translations/dealerCustomerForm";

import DealerLogin from "../components/dealer/DealerLogin";
import { DealerManualUpdateBanner, useManualUpdate } from "../components/dealer/DealerManualUpdateBanner";
import DealerHeader from "../components/dealer/DealerHeader";
import DealerDashboardView from "../components/dealer/DealerDashboardView";
import DealerCustomerForm from "../components/dealer/DealerCustomerForm";
import DealerCustomersList from "../components/dealer/DealerCustomersList";
import DealerSellersView from "../components/dealer/DealerSellersView";
import DealerReportView from "../components/dealer/DealerReportView";
import DealerManualsView from "../components/dealer/DealerManualsView";
import DealerChangeRequestsView from "../components/dealer/DealerChangeRequestsView";
import { MFAChallengeModal, MFASettingsCard, MFANudge } from "../components/shared/MFA";
import { useDealerNotifications } from "../hooks/useDealerNotifications";
import { useDealerSellers } from "../hooks/useDealerSellers";

// ISO codes of the 18 countries supported by the dealer New-Customer wizard.
// Names are computed at render-time via getLocalizedCountryName(lang) so the
// dropdown / banner shows e.g. "Polska" for PL-UI dealers and "Polonia" for
// IT-UI dealers — Fabrizio CR 23/02/2026 (i18n leak on the New Customer
// wizard's country banner).
const COUNTRY_CODES = [
  'IT', 'UK', 'FR', 'ES', 'DE', 'NL', 'BE', 'PT', 'PL',
  'SE', 'NO', 'FI', 'DK', 'CZ', 'GR', 'CH', 'GBZ', 'IRL',
];

// Browser language → internal portal language code (req. Fabrizio
// 07/07/2026: the login page must default to the visitor's browser
// language instead of a residual/hardcoded value).
const BROWSER_LANG_TO_CODE = {
  it: 'IT', en: 'UK', fr: 'FR', es: 'ES', de: 'DE', nl: 'NL', pt: 'PT',
  pl: 'PL', sv: 'SE', no: 'NO', nb: 'NO', nn: 'NO', fi: 'FI', da: 'DK',
  cs: 'CZ', el: 'GR',
};

const _initialDealerLang = () => {
  try {
    const saved = localStorage.getItem('dealer_lang');
    // Honour the saved language only for an explicit manual choice or
    // an active session being restored (avoids a language flash before
    // the post-login country sync). Otherwise follow the browser.
    if (saved && (localStorage.getItem('dealer_lang_manual') || localStorage.getItem('dealer_token'))) {
      return saved;
    }
    const bl = (navigator.language || '').slice(0, 2).toLowerCase();
    return BROWSER_LANG_TO_CODE[bl] || saved || 'UK';
  } catch (_) {
    return 'IT';
  }
};

const DealerPortal = () => {
  const [dealerToken, setDealerToken] = useState(localStorage.getItem('dealer_token'));
  const [dealer, setDealer] = useState(null);
  const [dealerRole, setDealerRole] = useState(localStorage.getItem('dealer_role') || 'dealer');
  const [lang, setLang] = useState(_initialDealerLang);
  // Merge the base dealer translations with the wizard-specific i18n pack
  // so every child component (lists, sellers, performance, etc.) can
  // access the full string catalogue under a single `t` prop. Wizard
  // keys are intentionally placed first so portal-level keys override
  // them when there's a collision (e.g. `firstName` exists in both).
  const tForm = getDealerFormI18n(lang);
  const tBase = getDealerTranslation(lang);
  const t = { ...tForm, ...tBase };
  // Country list with names localised into the dealer's active UI language.
  // Re-computed only when `lang` changes (cheap: 18-row map).
  const COUNTRIES = useMemo(
    () => COUNTRY_CODES.map((code) => ({ code, name: getLocalizedCountryName(code, lang) })),
    [lang]
  );
  const [view, setView] = useState('dashboard');
  const [customers, setCustomers] = useState([]);
  // Customer-list visibility scope: "mine" = only customers assigned to
  // me, "all" = every customer of the dealer (so sellers can find
  // colleagues' customers when needed — Fabrizio CR 26/02/2026).
  // Default = "default" which preserves the historical per-role
  // behaviour (seller=mine, manager=all).
  const [customerScope, setCustomerScope] = useState('default');
  const [stats, setStats] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  // MFA challenge state: when set, the login response was
  // `{mfa_required:true, mfa_method, mfa_challenge_token}` and we
  // mount <MFAChallengeModal /> instead of letting the user proceed.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [formData, setFormData] = useState({ customer_type: 'private', first_name: '', last_name: '', company_name: '', tax_code: '', vat_number: '', email: '', phone: '', phone_prefix: '+39', country: 'IT', vehicle_brand: '', vehicle_model: '', vehicle_plate: '', vehicle_vin: '', service_type: 'LoJack Tracker Passive 1' });
  const [creating, setCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dealerManuals, setDealerManuals] = useState([]);

  const [sellerReport, setSellerReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Digest settings
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);

  // Target management
  const [globalTarget, setGlobalTarget] = useState(0);
  const [editingGlobalTarget, setEditingGlobalTarget] = useState(false);
  const [tempGlobalTarget, setTempGlobalTarget] = useState('');
  const [editingSellerTarget, setEditingSellerTarget] = useState(null);
  const [tempSellerTarget, setTempSellerTarget] = useState('');

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState(null);

  // Zorzutti CR 29/07/2026 — change-requests badge counter.
  // Polled every 60s so a dealer with the portal open notices new
  // requests without waiting for the notification email. Reset to 0
  // when the token disappears (logout) to avoid leaking a stale badge.
  const [changeRequestsPending, setChangeRequestsPending] = useState(0);

  // Zorzutti CR 31/07/2026 — deep-link support. When the dealer clicks
  // the "Apri il portale dealer" CTA in a wallet-correction notification
  // email the URL looks like:
  //   /dealer?redirect=change_requests&request_id=<uuid>
  // We remember the target view + request id here so that both the
  // "not-yet-logged-in" (survive the login handshake) and the
  // "already-logged-in" flows land the dealer on the right card. Once
  // consumed the state is cleared and the query string is stripped
  // from the URL so a refresh does NOT retrigger the redirect.
  const [pendingDeepLink, setPendingDeepLink] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const redirect = p.get('redirect');
      const requestId = p.get('request_id');
      if (redirect) return { redirect, requestId: requestId || null };
    } catch (_) { /* URLSearchParams unsupported → no deep-link */ }
    return null;
  });

  // ─── Sub-hooks (notifications + sellers management) ───
  const {
    notifications, unreadCount, showNotifications, setShowNotifications,
    notifRef, fetchNotifications, markNotificationRead, markAllRead,
  } = useDealerNotifications({ dealerToken, enabled: dealerRole === 'dealer' });
  const manualUpdate = useManualUpdate(dealerToken, lang);

  const {
    sellers, sellerForm, setSellerForm, creatingSeller, showSellerForm,
    setShowSellerForm, fetchSellers,
    handleCreateSeller, handleToggleSeller, handleDeleteSeller, handleResendSetup,
  } = useDealerSellers({ dealerToken, t, mode: "seller" });

  // Zorzutti CR 12 Feb 2026 — Back-Office is now a separate top-level
  // menu item, not a checkbox inside the Sellers form. Each tab has its
  // own form state + filtered list so the two roles stay visually and
  // operationally distinct ("a Back-Office employee is NOT a Seller").
  const {
    sellers: backOfficeUsers,
    sellerForm: backOfficeForm,
    setSellerForm: setBackOfficeForm,
    creatingSeller: creatingBackOffice,
    showSellerForm: showBackOfficeForm,
    setShowSellerForm: setShowBackOfficeForm,
    fetchSellers: fetchBackOfficeUsers,
    handleCreateSeller: handleCreateBackOffice,
    handleToggleSeller: handleToggleBackOffice,
    handleDeleteSeller: handleDeleteBackOffice,
    handleResendSetup: handleResendBackOfficeSetup,
  } = useDealerSellers({ dealerToken, t, mode: "back_office" });

  // ─── Data fetching ───
  const fetchDealerManuals = async () => { try { const res = await axios.get(`${API}/manuals?role=dealer`); setDealerManuals(res.data); } catch { /* */ } };
  const fetchStats = async (tk) => { try { const res = await dealerApi.get(`/dealer/stats`, { token: tk || dealerToken }); setStats(res.data); } catch { /* */ } };
  const fetchCustomers = async (tk, scopeOverride) => {
    try {
      const tokenToUse = tk || dealerToken;
      const scopeToUse = scopeOverride || customerScope;
      const res = await dealerApi.get(
        `/dealer/customers`,
        { token: tokenToUse, config: { params: { scope: scopeToUse } } },
      );
      setCustomers(res.data);
    } catch { /* */ }
  };
  const fetchSellerReport = async (tk) => { setLoadingReport(true); try { const res = await dealerApi.get(`/dealer/sellers/report`, { token: tk || dealerToken }); setSellerReport(res.data); } catch { /* */ } finally { setLoadingReport(false); } };
  const fetchDigestSettings = async (tk) => { try { const res = await dealerApi.get(`/dealer/digest-settings`, { token: tk || dealerToken }); setDigestEnabled(res.data.weekly_digest); } catch { /* */ } };
  const fetchTargets = async (tk) => { try { const res = await dealerApi.get(`/dealer/targets`, { token: tk || dealerToken }); setGlobalTarget(res.data.global_target || 0); } catch { /* */ } };
  const fetchLeaderboard = async (tk) => { try { const res = await dealerApi.get(`/dealer/sellers/leaderboard`, { token: tk || dealerToken }); setLeaderboard(res.data); } catch { /* */ } };

  // ─── Handlers ───
  const toggleDigest = async () => {
    setDigestLoading(true);
    try { const res = await dealerApi.put(`/dealer/digest-settings`, { token: dealerToken }, { weekly_digest: !digestEnabled }); setDigestEnabled(res.data.weekly_digest); toast.success(res.data.weekly_digest ? t.digestEnabled : t.digestDisabled); }
    catch { toast.error(t.saveError); } finally { setDigestLoading(false); }
  };

  const sendDigestPreview = async () => {
    setSendingPreview(true);
    try { await dealerApi.post(`/dealer/digest-preview`, { token: dealerToken }); toast.success(t.digestSent); }
    catch { toast.error(t.sendError); } finally { setSendingPreview(false); }
  };

  const saveGlobalTarget = async () => {
    const val = parseInt(tempGlobalTarget);
    if (isNaN(val) || val < 0) { toast.error(t.enterValidNumber || 'Inserisci un numero valido'); return; }
    try { await dealerApi.put(`/dealer/targets`, { token: dealerToken }, { monthly_target: val }); setGlobalTarget(val); setEditingGlobalTarget(false); toast.success(`${t.targetUpdated}: ${val} ${t.clientsMonth}`); fetchSellerReport(); }
    catch { toast.error(t.saveError); }
  };

  const saveSellerTarget = async (sellerId) => {
    const raw = tempSellerTarget.trim();
    const val = raw === '' ? null : parseInt(raw);
    if (val !== null && (isNaN(val) || val < 0)) { toast.error(t.saveError); return; }
    try { await dealerApi.put(`/dealer/sellers/${sellerId}/target`, { token: dealerToken }, { monthly_target: val }); setEditingSellerTarget(null); toast.success(val !== null ? `${t.customTarget}: ${val}` : t.targetRemoved); fetchSellerReport(); }
    catch { toast.error(t.saveError); }
  };

  // Finalises the dealer session after a successful login (with or
  // without MFA). Pulled out of `handleLogin` so the MFA challenge
  // resolution path can reuse exactly the same persistence logic.
  const completeLogin = (payload) => {
    const token = payload.token;
    const role = payload.dealer?.role || 'dealer';
    const dealerCountry = payload.dealer?.country;
    localStorage.setItem('dealer_token', token);
    localStorage.setItem('dealer_role', role);
    localStorage.setItem('dealer_email', payload.dealer?.email || '');
    setDealerToken(token); setDealer(payload.dealer); setDealerRole(role);
    if (dealerCountry) {
      setFormData((prev) => (
        (!prev || prev.country === 'IT')
          ? { ...prev, country: dealerCountry, phone_prefix: getPrefixByCountry(dealerCountry) }
          : prev
      ));
    }
    if (dealerCountry && !localStorage.getItem('dealer_lang_manual')) {
      setLang(dealerCountry); localStorage.setItem('dealer_lang', dealerCountry);
    }
    if (payload.dealer?.must_change_password) {
      toast.info("Devi impostare una nuova password personale al primo accesso.");
      window.location.href = `/dealer/change-password?token=${token}`;
      return;
    }
    fetchStats(token); fetchCustomers(token); fetchDealerManuals(); fetchLeaderboard(token);
    if (role === 'dealer') { fetchSellers(token); }
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError('');
    try {
      // Zorzutti CR 05/08/2026 — send X-Dealer-Lang so the backend
      // can return already-localised 401/403 detail strings instead
      // of the hardcoded Italian ``Credenziali non valide``. Mobile
      // apps and third-party API clients rely on the same header
      // (or Accept-Language) — see services/backend_errors_i18n.py.
      const res = await axios.post(`${API}/dealer/login`, loginData, {
        headers: { 'X-Dealer-Lang': (lang || 'UK').toUpperCase() },
      });
      if (res.data?.mfa_required) {
        // Pause the flow and show the MFA challenge modal. The modal
        // hits /api/mfa/verify and on success calls back with the
        // real login payload (token + dealer dict).
        setMfaChallenge({ token: res.data.mfa_challenge_token, method: res.data.mfa_method });
        return;
      }
      completeLogin(res.data);
    } catch (err) {
      // FastAPI + Pydantic v2 return validation errors as an array
      // of dicts ({type,loc,msg,input,ctx}); rendering that array as
      // React child throws "Objects are not valid as a React child".
      // Coerce to string defensively.
      //
      // Zorzutti CR 05/08/2026 — on HTTP 401/403 the backend returns a
      // hardcoded Italian ``Credenziali non valide`` detail which then
      // leaks into non-IT dealer portals (polish dealer reported the
      // Italian sentence on the PL login screen). Ignore the raw detail
      // for these status codes and use the localized ``t.loginError``
      // instead. Other statuses (422 field errors, 400, 500) keep the
      // server-side detail because they're diagnostic.
      const status = err.response?.status;
      const raw = err.response?.data?.detail;
      let msg;
      if (status === 401 || status === 403) {
        msg = t.loginError;
      } else if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(e => e?.msg || JSON.stringify(e)).join('; ');
      else if (raw && typeof raw === 'object') msg = raw.msg || JSON.stringify(raw);
      else msg = t.loginError;
      setLoginError(msg);
    }
  };

  const changeLang = (code) => { setLang(code); localStorage.setItem('dealer_lang', code); localStorage.setItem('dealer_lang_manual', '1'); };

  const handleLogout = () => {
    localStorage.removeItem('dealer_token'); localStorage.removeItem('dealer_role');
    localStorage.removeItem('dealer_email'); localStorage.removeItem('dealer_lang_manual');
    setDealerToken(null); setDealer(null); setDealerRole('dealer');
  };

  // VIN duplicate modal — opened when backend returns 409
  // vin_already_enrolled. Holds the existing customer payload so we
  // can offer "Apri scheda cliente esistente" CTA inline.
  const [vinDupeInfo, setVinDupeInfo] = useState(null);

  // Wallet QR modal — opened after successful customer creation when
  // the country's `communication_channels` policy is SMS-only (or both
  // and the dealer wants to show the link in-person). Powered by the
  // wallet_url/sms_short_link returned by the create endpoint. Lets
  // the dealer show their screen for the customer to scan the wallet
  // link on the spot — useful when the customer's email is unreliable
  // (req. Mara Capelli stand-up 17/06/2026).
  const [walletQrInfo, setWalletQrInfo] = useState(null);

  const handleCreateCustomer = async (e) => {
    if (e) e.preventDefault();
    if (!showConfirm) { setShowConfirm(true); return; }
    setShowConfirm(false); setCreating(true);
    try {
      const resp = await dealerApi.post(`/dealer/customers`, { token: dealerToken }, formData);
      toast.success(t.customerCreated);
      const channel = resp.data?.communication_channels || "both";
      // Show the QR modal whenever the country is in SMS mode (sms or
      // both). For email-only countries the customer always has the
      // mailbox link, so no inline QR is needed.
      if (resp.data?.wallet_url && (channel === "sms" || channel === "both")) {
        setWalletQrInfo({
          wallet_url: resp.data.wallet_url,
          sms_short_link: resp.data.sms_short_link,
          channel,
          customer_email: formData.email,
          customer_phone: `${formData.phone_prefix} ${formData.phone}`.trim(),
        });
      }
      setFormData({ customer_type: 'private', first_name: '', last_name: '', company_name: '', tax_code: '', vat_number: '', email: '', phone: '', phone_prefix: getPrefixByCountry(dealer?.country || 'IT'), country: dealer?.country || 'IT', vehicle_brand: '', vehicle_model: '', vehicle_plate: '', vehicle_vin: '', service_type: 'LoJack Tracker Passive 1' });
      fetchCustomers(); fetchStats(); setView('customers');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      // 409 vin_already_enrolled → dedicated modal (not a fleeting toast).
      // Backend ships full info on the existing record so the dealer can
      // decide what to do (open it, change VIN, etc.) without guessing.
      if (status === 409 && detail && typeof detail === 'object' && detail.error === 'vin_already_enrolled') {
        setVinDupeInfo(detail);
      } else {
        // Backend may return a structured error dict (e.g. vin_invalid)
        // or a plain string. Normalise to a user-friendly toast.
        let msg = t.createError;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (detail && typeof detail === 'object') {
          msg = detail.message || t.createError;
        }
        toast.error(msg);
      }
    }
    finally { setCreating(false); }
  };

  // ─── Effects ───
  useEffect(() => {
    if (dealerToken) {
      // After a hard reload (F5 / new tab) we only have the token from
      // localStorage; `dealer` is null until the user re-logs in. That
      // produced a subtle bug: `dealer?.country || 'IT'` fell back to
      // 'IT' for every non-Italian dealer, pre-filling the New Customer
      // form with the wrong country (Fabrizio report 02/06/2026, Polish
      // dealer seeing "Italy"). We rehydrate the profile from /me here.
      if (!dealer) {
        dealerApi.get(`/dealer/me`, { token: dealerToken })
          .then((res) => {
            const d = res.data || {};
            setDealer(d);
            setDealerRole(d.role || 'dealer');
            // Seed the New Customer form with the dealer's country so
            // the picker shows the correct flag/prefix on first paint.
            if (d.country) {
              setFormData((prev) => (
                (!prev || prev.country === 'IT')
                  ? { ...prev, country: d.country, phone_prefix: getPrefixByCountry(d.country) }
                  : prev
              ));
              // Honour any explicit manual language override, otherwise
              // sync the UI language with the dealer's country.
              if (!localStorage.getItem('dealer_lang_manual')) {
                setLang(d.country);
                localStorage.setItem('dealer_lang', d.country);
              }
            }
          })
          .catch(() => {
            // Token expired / invalid — drop it and let the login form
            // re-appear instead of leaving the user stuck on a half-
            // hydrated portal.
            localStorage.removeItem('dealer_token');
            setDealerToken(null);
          });
      }
      fetchStats(); fetchCustomers(); fetchDealerManuals(); fetchLeaderboard();
      if (dealerRole === 'dealer') { fetchSellers(); fetchBackOfficeUsers(); fetchNotifications(); fetchDigestSettings(); fetchTargets(); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerToken]);

  // Zorzutti CR 29/07/2026 — polling for change-requests pending count.
  // Kept as a separate effect (not inside the main token effect) so it
  // cleanly restarts on token change and doesn't interact with the rest
  // of the initial-load fan-out. 60s cadence: a good balance between
  // responsiveness and dealer server load (18 SOC × N dealers).
  useEffect(() => {
    if (!dealerToken) { setChangeRequestsPending(0); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await dealerApi.get(
          `/dealer/customer-update-requests/count`,
          { token: dealerToken },
        );
        if (!cancelled) setChangeRequestsPending(res.data?.pending || 0);
      } catch { /* silent — polling should never toast */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dealerToken]);

  // Zorzutti CR 31/07/2026 — consume the deep-link once the dealer is
  // authenticated. Fires either immediately (already logged in, hard
  // refresh with the link) or right after a successful login handshake
  // (localStorage token appears). We do the "consume" in an effect so
  // that both flows converge on the same code path.
  //
  // After the redirect we scrub the query string via history.replaceState
  // so that a subsequent F5 doesn't re-trigger the jump (the request may
  // be gone by then, either approved or rejected, and re-landing on the
  // now-empty change-requests view would look like a broken link).
  useEffect(() => {
    if (!dealerToken || !pendingDeepLink) return;
    const map = { change_requests: 'change_requests' };
    const target = map[pendingDeepLink.redirect];
    if (target) setView(target);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect');
      url.searchParams.delete('request_id');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (_) { /* replaceState unsupported → leave URL as-is */ }
    // Keep pendingDeepLink.requestId around for the child view to
    // consume — clear the `redirect` half so this effect doesn't re-run
    // on the next render but we still remember the target card id.
    // Guard against re-firing: only clone when redirect is still set,
    // otherwise the object-identity change would trigger this same
    // effect again on the next render (React "Maximum update depth
    // exceeded" warning caught by iteration_80 test agent).
    setPendingDeepLink((prev) =>
      (prev && prev.redirect) ? { ...prev, redirect: null } : prev
    );
  }, [dealerToken, pendingDeepLink]);

  // ─── Tabs ───
  const getTabs = () => {
    const tabs = [
      { key: 'dashboard', label: t.dashboard, icon: BarChart3 },
      { key: 'new', label: t.newCustomer, icon: Plus },
      { key: 'customers', label: t.myCustomers, icon: Users },
    ];
    if (dealerRole === 'dealer') {
      tabs.push({ key: 'sellers', label: t.sellers, icon: UserPlus });
      tabs.push({ key: 'back_office', label: t.backOffice || 'Back-Office', icon: Briefcase });
      tabs.push({ key: 'report', label: t.report, icon: FileBarChart });
    }
    tabs.push({
      key: 'change_requests',
      label: t.changeRequestsTab || 'Change Requests',
      icon: Inbox,
      badge: changeRequestsPending,
    });
    if (dealerManuals.length > 0) {
      tabs.push({ key: 'manuals', label: t.manuals, icon: BookOpen });
    }
    tabs.push({ key: 'security', label: (t.security || 'Sicurezza'), icon: Shield });
    return tabs;
  };

  // ─── Login screen ───
  if (!dealerToken) {
    return (
      <>
        <DealerLogin loginData={loginData} setLoginData={setLoginData} loginError={loginError} handleLogin={handleLogin} lang={lang} changeLang={changeLang} t={t} />
        {mfaChallenge && (
          <MFAChallengeModal
            challengeToken={mfaChallenge.token}
            method={mfaChallenge.method}
            lang={lang}
            onSuccess={(payload) => { setMfaChallenge(null); completeLogin(payload); }}
            onCancel={() => setMfaChallenge(null)}
          />
        )}
      </>
    );
  }

  // ─── Main portal ───
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="dealer-portal">
      <MFANudge authHeader={{ Authorization: `Bearer ${dealerToken}` }} userEmail={dealer?.email} lang={lang} />
      <DealerHeader
        dealer={dealer} stats={stats} dealerRole={dealerRole}
        lang={lang} changeLang={changeLang} handleLogout={handleLogout}
        notifications={notifications} unreadCount={unreadCount}
        showNotifications={showNotifications} setShowNotifications={setShowNotifications}
        fetchNotifications={fetchNotifications} markNotificationRead={markNotificationRead}
        markAllRead={markAllRead} notifRef={notifRef} t={t}
        manualUpdate={manualUpdate.update} onManualOpen={manualUpdate.dismiss}
      />

      <div className="max-w-6xl mx-auto px-4 pt-4">
        <DealerManualUpdateBanner update={manualUpdate.update} dismiss={manualUpdate.dismiss} lang={lang} />
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {getTabs().map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => {
                setView(tab.key);
                if (tab.key === 'report') { if (!sellerReport) fetchSellerReport(); fetchLeaderboard(); }
                if (tab.key === 'sellers') fetchSellers();
                if (tab.key === 'back_office') fetchBackOfficeUsers();
              }}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view === tab.key ? 'bg-lojack-red text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                data-testid={`dealer-tab-${tab.key}`}>
                <Icon size={16} /> {tab.label}
                {tab.badge > 0 && (
                  <span
                    className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                      view === tab.key
                        ? 'bg-white text-lojack-red'
                        : 'bg-lojack-red text-white'
                    }`}
                    data-testid={`dealer-tab-${tab.key}-badge`}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* View content */}
        {view === 'dashboard' && (
          <DealerDashboardView
            stats={stats} dealerRole={dealerRole} setView={setView}
            globalTarget={globalTarget} leaderboard={leaderboard}
            digestEnabled={digestEnabled} digestLoading={digestLoading}
            sendingPreview={sendingPreview} toggleDigest={toggleDigest}
            sendDigestPreview={sendDigestPreview} t={t}
            dealerToken={dealerToken}
            dealerCountry={dealer?.country}
          />
        )}

        {view === 'new' && (
          <DealerCustomerForm
            formData={formData} setFormData={setFormData}
            handleCreateCustomer={handleCreateCustomer} creating={creating}
            showConfirm={showConfirm} setShowConfirm={setShowConfirm}
            countries={COUNTRIES} t={t} lang={lang}
            dealerCountry={dealer?.country || 'IT'}
            dealerRole={dealerRole}
            dealerToken={dealerToken}
          />
        )}

        {view === 'customers' && (
          <DealerCustomersList
            customers={customers}
            fetchCustomers={fetchCustomers}
            t={t}
            dealerToken={dealerToken}
            scope={customerScope}
            onScopeChange={(newScope) => {
              setCustomerScope(newScope);
              fetchCustomers(undefined, newScope);
            }}
            dealerRole={dealerRole}
          />
        )}

        {view === 'sellers' && dealerRole === 'dealer' && (
          <DealerSellersView
            mode="seller"
            sellers={sellers} sellerForm={sellerForm} setSellerForm={setSellerForm}
            creatingSeller={creatingSeller} showSellerForm={showSellerForm}
            setShowSellerForm={setShowSellerForm} handleCreateSeller={handleCreateSeller}
            handleToggleSeller={handleToggleSeller} handleDeleteSeller={handleDeleteSeller}
            handleResendSetup={handleResendSetup} fetchSellers={fetchSellers} t={t}
          />
        )}

        {view === 'back_office' && dealerRole === 'dealer' && (
          <DealerSellersView
            mode="back_office"
            sellers={backOfficeUsers} sellerForm={backOfficeForm} setSellerForm={setBackOfficeForm}
            creatingSeller={creatingBackOffice} showSellerForm={showBackOfficeForm}
            setShowSellerForm={setShowBackOfficeForm} handleCreateSeller={handleCreateBackOffice}
            handleToggleSeller={handleToggleBackOffice} handleDeleteSeller={handleDeleteBackOffice}
            handleResendSetup={handleResendBackOfficeSetup} fetchSellers={fetchBackOfficeUsers} t={t}
          />
        )}

        {view === 'report' && dealerRole === 'dealer' && (
          <DealerReportView
            sellerReport={sellerReport} loadingReport={loadingReport}
            fetchSellerReport={fetchSellerReport} globalTarget={globalTarget}
            editingGlobalTarget={editingGlobalTarget} setEditingGlobalTarget={setEditingGlobalTarget}
            tempGlobalTarget={tempGlobalTarget} setTempGlobalTarget={setTempGlobalTarget}
            saveGlobalTarget={saveGlobalTarget} editingSellerTarget={editingSellerTarget}
            setEditingSellerTarget={setEditingSellerTarget} tempSellerTarget={tempSellerTarget}
            setTempSellerTarget={setTempSellerTarget} saveSellerTarget={saveSellerTarget}
            leaderboard={leaderboard} fetchLeaderboard={fetchLeaderboard} t={t}
          />
        )}

        {view === 'manuals' && <DealerManualsView dealerManuals={dealerManuals} />}

        {view === 'change_requests' && (
          <DealerChangeRequestsView
            dealerToken={dealerToken}
            onCountChange={setChangeRequestsPending}
            initialRequestId={pendingDeepLink?.requestId || null}
            onDeepLinkConsumed={() => setPendingDeepLink(null)}
            t={t}
          />
        )}

        {view === 'security' && (
          <div className="max-w-2xl">
            <MFASettingsCard
              authHeader={{ Authorization: `Bearer ${dealerToken}` }}
              userEmail={dealer?.email}
            />
          </div>
        )}
      </div>

      {/* VIN-duplicate modal — opened when POST /dealer/customers
          returns 409 vin_already_enrolled. Replaces the previous
          5-second toast that users routinely missed. */}
      {vinDupeInfo && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setVinDupeInfo(null)}
          data-testid="vin-dupe-modal"
        >
          <div
            className="bg-[#141414] border border-lojack-clause-500/40 rounded-2xl p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-lojack-clause-500/10 flex items-center justify-center">
                  <span className="text-lojack-clause-400 text-xl">⚠</span>
                </div>
                <h3 className="text-white font-semibold text-lg">
                  {t.vinAlreadyEnrolledTitle || 'VIN già registrato'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setVinDupeInfo(null)}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {(t.vinAlreadyEnrolledBody || 'Il VIN {vin} risulta già registrato nel sistema. Un veicolo non può essere associato a due clienti diversi.').replace('{vin}', vinDupeInfo.vin || formData.vehicle_vin)}
            </p>
            <div className="bg-[#0A0A0A] rounded-lg p-3 mb-4 border border-gray-800 space-y-1.5">
              <div className="flex justify-between gap-3 text-xs">
                <span className="text-gray-500">{t.existingCustomerLabel || 'Cliente esistente'}:</span>
                <span className="text-white font-medium truncate">{vinDupeInfo.existing_customer_name || '—'}</span>
              </div>
              {vinDupeInfo.existing_customer_email && (
                <div className="flex justify-between gap-3 text-xs">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-300 truncate">{vinDupeInfo.existing_customer_email}</span>
                </div>
              )}
              {vinDupeInfo.existing_customer_created_at && (
                <div className="flex justify-between gap-3 text-xs">
                  <span className="text-gray-500">{t.createdOnLabel || 'Creato il'}:</span>
                  <span className="text-gray-300">{new Date(vinDupeInfo.existing_customer_created_at).toLocaleDateString(lang.toLowerCase())}</span>
                </div>
              )}
              <div className="flex justify-between gap-3 text-xs">
                <span className="text-gray-500">{t.contractStatusLabel || 'Contratto'}:</span>
                <span className={vinDupeInfo.existing_customer_contract_signed ? 'text-emerald-400' : 'text-lojack-clause-400'}>
                  {vinDupeInfo.existing_customer_contract_signed ? (t.signedLabel || 'Firmato') : (t.pendingSignatureLabel || 'In attesa di firma')}
                </span>
              </div>
            </div>
            {!vinDupeInfo.existing_customer_same_dealer && (
              <p className="text-xs text-gray-500 italic mb-4">
                {t.vinDupeOtherDealer || 'Il veicolo è registrato presso un altro concessionario. Contatta il supporto LoJack se ritieni si tratti di un errore.'}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setVinDupeInfo(null)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg flex-1 min-w-[140px]"
                data-testid="vin-dupe-change-vin-btn"
              >
                {t.changeVinBtn || 'Modifica VIN'}
              </button>
              {vinDupeInfo.existing_customer_same_dealer && vinDupeInfo.existing_customer_id && (
                <button
                  type="button"
                  onClick={() => { setVinDupeInfo(null); setView('customers'); }}
                  className="px-4 py-2 bg-lojack-red hover:bg-lojack-red-hover text-white text-sm rounded-lg flex-1 min-w-[140px] font-medium"
                  data-testid="vin-dupe-open-customer-btn"
                >
                  {t.openExistingCustomerBtn || 'Apri scheda cliente'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wallet QR modal — surfaces the just-created customer's wallet
          link as a scannable QR + copyable text. Shown when SMS mode
          is active (req. Mara stand-up 17/06/2026), but harmless on
          email-only flows too. Provides 2 affordances:
          1. Scan QR with customer's phone → opens wallet immediately
          2. Copy short link → dealer pastes into a chat/WhatsApp etc. */}
      {walletQrInfo && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setWalletQrInfo(null)}
          data-testid="wallet-qr-modal-overlay"
        >
          <div
            className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="wallet-qr-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <QrCode size={20} className="text-emerald-400" />
                {t.walletQrTitle || 'Link wallet — QR pronto da mostrare'}
              </h3>
              <button
                onClick={() => setWalletQrInfo(null)}
                className="text-gray-400 hover:text-white"
                data-testid="wallet-qr-close-btn"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-300 text-sm leading-relaxed">
                {(walletQrInfo.channel === 'sms'
                  ? (t.walletQrSmsModeBody || 'Il cliente in questo paese riceve la card via SMS. Mostra questo QR al cliente per aprire subito il wallet sul suo telefono — senza attendere il messaggio.')
                  : (t.walletQrBothModeBody || 'Email e SMS inviati. Se il cliente è davanti a te, può scansionare questo QR per aprire la card immediatamente.'))}
              </p>

              <div className="bg-white rounded-2xl p-4 flex items-center justify-center" data-testid="wallet-qr-svg-wrap">
                <QRCodeSVG
                  value={walletQrInfo.sms_short_link || walletQrInfo.wallet_url}
                  size={220}
                  level="M"
                  includeMargin={false}
                />
              </div>

              <div className="space-y-2">
                <label className="text-gray-500 text-xs uppercase tracking-wide flex items-center gap-1.5">
                  <MessageSquare size={11} />
                  {t.walletQrShortLinkLabel || 'Link breve (per WhatsApp / chat)'}
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={walletQrInfo.sms_short_link || walletQrInfo.wallet_url}
                    className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2 text-xs font-mono text-gray-200 truncate"
                    data-testid="wallet-qr-link-input"
                  />
                  <button
                    onClick={() => {
                      const link = walletQrInfo.sms_short_link || walletQrInfo.wallet_url;
                      navigator.clipboard?.writeText(link);
                      toast.success(t.walletQrCopied || 'Link copiato');
                    }}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
                    data-testid="wallet-qr-copy-btn"
                  >
                    <Copy size={12} />
                    {t.walletQrCopyBtn || 'Copia'}
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-800 pt-3">
                {walletQrInfo.customer_phone && (
                  <p>{t.walletQrSmsRecipient || 'SMS inviato a'}: <strong className="text-gray-300">{walletQrInfo.customer_phone}</strong></p>
                )}
                {walletQrInfo.customer_email && (
                  <p>{t.walletQrEmailRecipient || 'Email inviata a'}: <strong className="text-gray-300">{walletQrInfo.customer_email}</strong></p>
                )}
              </div>

              <button
                onClick={() => setWalletQrInfo(null)}
                className="w-full mt-2 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
                data-testid="wallet-qr-done-btn"
              >
                {t.walletQrDoneBtn || 'Chiudi'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center py-8 mt-8"><a href="https://telcavoip.ai" className="text-gray-600 text-xs hover:text-lojack-red transition-colors">Powered by TelcaVoIP AI Engine</a></div>
    </div>
  );
};

export default DealerPortal;
